import { authenticatedFetch } from "./apiClient";
import type {
  BillingCheckoutInterval,
  BillingCheckoutResponse,
  BillingPlan,
  BillingPortalResponse,
  BillingStatusResponse,
} from "../types/billing";
import { isTier1AccessGranted, normalizeBillingStatusResponse } from "../types/billing";
import { parseApiError } from "../utils/parseApiError";
import type { ParsedApiError } from "../utils/parseApiError";
import {
  ApiRequestError,
  isApiRequestError,
  parseJsonOrThrow,
} from "../utils/apiRequestError";

export class BillingApiError extends ApiRequestError {
  /** Legacy billing error code (e.g. `require_tier1_access`, `no_stripe_customer`). */
  code?: string;

  constructor(message: string, status: number, parsed: ParsedApiError, code?: string) {
    super(message, status, parsed);
    this.name = "BillingApiError";
    this.code =
      code ??
      (typeof parsed.rawBody === "object" &&
      parsed.rawBody &&
      typeof (parsed.rawBody as Record<string, unknown>).code === "string"
        ? String((parsed.rawBody as Record<string, unknown>).code)
        : parsed.slug !== "http_error"
          ? parsed.slug
          : undefined);
  }
}

function throwBillingApiError(
  status: number,
  parsed: ParsedApiError,
  fallbackMessage: string,
  code?: string
): never {
  const message = parsed.message?.trim() || fallbackMessage;
  if (status === 401) {
    throw new BillingApiError("Session expired. Please log in again.", 401, {
      slug: "auth.token_expired",
      message: "Session expired. Please log in again.",
    });
  }
  if (status === 402) {
    throw new BillingApiError(
      message || "Subscription required.",
      402,
      { ...parsed, slug: parsed.slug !== "http_error" ? parsed.slug : "tier.subscription_required" },
      code || "require_tier1_access"
    );
  }
  throw new BillingApiError(message, status, parsed, code);
}

async function parseBillingJsonOrThrow<T>(response: Response, fallbackMessage: string): Promise<T> {
  try {
    return await parseJsonOrThrow<T>(response, fallbackMessage);
  } catch (err) {
    if (err instanceof ApiRequestError && !(err instanceof BillingApiError)) {
      throw new BillingApiError(err.message, err.status, {
        slug: err.slug,
        message: err.message,
        details: err.details,
        retryAfterSeconds: err.retryAfterSeconds,
      });
    }
    throw err;
  }
}

/**
 * POST /api/v1/billing/checkout — Stripe Checkout for Standard or Plus.
 * `interval` defaults to monthly on the server if omitted; always send it from the UI.
 */
export async function startPlanCheckout(
  plan: BillingPlan,
  interval: BillingCheckoutInterval = "month"
): Promise<BillingCheckoutResponse> {
  const response = await authenticatedFetch("/v1/billing/checkout", {
    method: "POST",
    body: JSON.stringify({ plan, interval }),
  });
  return parseBillingJsonOrThrow<BillingCheckoutResponse>(
    response,
    `Failed to start checkout: ${response.status}`
  );
}

/**
 * POST /api/v1/billing/portal — returns Stripe Customer Portal URL
 */
export async function openBillingPortal(): Promise<BillingPortalResponse> {
  const response = await authenticatedFetch("/v1/billing/portal", {
    method: "POST",
    body: JSON.stringify({}),
  });
  return parseBillingJsonOrThrow<BillingPortalResponse>(
    response,
    `Failed to open billing portal: ${response.status}`
  );
}

/**
 * GET /api/v1/billing/status
 * Source of truth for paid access (`status` active/trialing). Do not use `in_trial`.
 * If the server returns 402/403 but the body still includes trialing/active
 * (e.g. global paywall middleware), we use that so subscribed users are not gated.
 */
export async function getBillingStatus(): Promise<BillingStatusResponse> {
  const response = await authenticatedFetch("/v1/billing/status");
  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    raw = null;
  }

  const normalized = normalizeBillingStatusResponse(raw);
  if (response.ok) {
    return normalized;
  }

  if (
    (response.status === 402 || response.status === 403) &&
    isTier1AccessGranted(normalized.status)
  ) {
    return normalized;
  }

  const parsed =
    raw && typeof raw === "object"
      ? await parseApiError(
          new Response(JSON.stringify(raw), {
            status: response.status,
            statusText: response.statusText,
          })
        )
      : await parseApiError(response);
  throwBillingApiError(
    response.status,
    parsed,
    `Failed to fetch billing status: ${response.status}`
  );
}

export interface TrialStatusResponse {
  in_trial: boolean;
  trial_end_at: string | null;
  trial_start_at?: string | null;
  subscription_status?: string | null;
  server_now?: string;
  timezone?: "UTC" | string;
}

function normalizeTrialStatusResponse(raw: unknown): TrialStatusResponse {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const inTrialRaw = o.in_trial ?? o.inTrial;
  const in_trial = Boolean(inTrialRaw);

  const trial_end_at =
    (o.trial_end_at === null || o.trialEndAt === null || o.trial_end === null)
      ? null
      : (o.trial_end_at ?? o.trialEndAt ?? o.trial_end ?? null);

  const trial_start_at =
    (o.trial_start_at ?? o.trialStartAt) === null ? null : (o.trial_start_at ?? o.trialStartAt ?? null);

  const subscription_status =
    (o.subscription_status ?? o.subscriptionStatus) === null
      ? null
      : (o.subscription_status ?? o.subscriptionStatus ?? null);

  const server_now = (o.server_now ?? o.serverNow) as string | undefined;
  const timezone = (o.timezone ?? o.tz) as TrialStatusResponse["timezone"] | undefined;

  return {
    in_trial,
    trial_end_at: typeof trial_end_at === "string" || trial_end_at === null ? (trial_end_at as any) : null,
    trial_start_at: typeof trial_start_at === "string" || trial_start_at === null ? trial_start_at : null,
    subscription_status:
      typeof subscription_status === "string" || subscription_status === null ? subscription_status : null,
    server_now: typeof server_now === "string" ? server_now : undefined,
    timezone: timezone ? timezone : undefined,
  };
}

/**
 * GET /api/billing/trial-status (also available as /api/v1/billing/trial-status)
 */
export async function getTrialStatus(): Promise<TrialStatusResponse> {
  const tryNonV1 = async (endpoint: string) => {
    const response = await authenticatedFetch(endpoint);
    let raw: unknown = null;
    try {
      raw = await response.json();
    } catch {
      raw = null;
    }
    if (response.ok) {
      return normalizeTrialStatusResponse(raw);
    }
    const parsed =
      raw && typeof raw === "object"
        ? await parseApiError(
            new Response(JSON.stringify(raw), {
              status: response.status,
              statusText: response.statusText,
            })
          )
        : await parseApiError(response);
    throwBillingApiError(
      response.status,
      parsed,
      `Failed to fetch trial status: ${response.status}`
    );
  };

  try {
    return await tryNonV1("/billing/trial-status");
  } catch {
    return await tryNonV1("/v1/billing/trial-status");
  }
}

export function isBillingAlreadySubscribedError(err: unknown): boolean {
  if (!isApiRequestError(err) && !(err instanceof BillingApiError)) return false;
  const status = (err as ApiRequestError).status;
  const slug = String((err as ApiRequestError).slug ?? "").toLowerCase();
  const code =
    err instanceof BillingApiError ? String(err.code ?? "").toLowerCase() : "";
  return (
    status === 409 &&
    (slug === "billing.already_subscribed" ||
      slug.includes("already_subscribed") ||
      code === "billing.already_subscribed" ||
      code.includes("already_subscribed"))
  );
}

export function isThreeMonthOfferEndedError(err: unknown): boolean {
  if (!isApiRequestError(err) && !(err instanceof BillingApiError)) return false;
  const slug = String((err as ApiRequestError).slug ?? "").toLowerCase();
  const code =
    err instanceof BillingApiError ? String(err.code ?? "").toLowerCase() : "";
  return (
    slug === "billing.three_month_offer_ended" ||
    code === "billing.three_month_offer_ended" ||
    slug.includes("three_month_offer_ended")
  );
}

export function isBillingPaywallError(err: unknown): err is BillingApiError {
  return err instanceof BillingApiError && err.status === 402;
}

/**
 * True when the backend indicates there is no Stripe customer yet (never completed checkout).
 * Kept strict: many portal 400s mention "customer" (e.g. configuration) and must not show "Subscribe first"
 * for users who are already trialing/active.
 */
export function isNoStripeCustomerPortalError(err: unknown): boolean {
  if (!(err instanceof BillingApiError) || err.status !== 400) return false;
  const code = (err.code ?? "").toLowerCase();
  const slug = String(err.slug ?? "").toLowerCase();
  if (
    code === "no_stripe_customer" ||
    code === "stripe_customer_missing" ||
    code === "customer_required" ||
    code === "billing.stripe.no_stripe_customer_yet" ||
    slug === "billing.stripe.no_stripe_customer_yet"
  ) {
    return true;
  }
  const d = `${err.detail ?? ""} ${err.message ?? ""}`.toLowerCase();
  return (
    d.includes("no stripe customer") ||
    d.includes("no customer id") ||
    d.includes("customer not found") ||
    d.includes("missing stripe customer") ||
    d.includes("has not completed checkout")
  );
}
